import { useEffect } from 'react';

import { useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';

import { getUserInfo } from '@pages/networking/Network/components/network/functions/getUserInfo';
import { ContactsRoutesPathEnum } from '@setup/router/routes/contacts/contactsRoutesPath.enum';
import SocketConnect from '@setup/socket';
import { PagePlaceEnum, PageViewAnalyticsEvents } from '@shared/analytics/page-view/page-view';
import useAnalyticsEventTracker from '@shared/hooks/analytics/useAnalyticsEventTracker';
import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';
import { setBreadcrumbs } from '@store/Common/commonThunk';
import { deleteNetworkUser } from '@store/network/networkSlice';
import { networkSocketCreatedContact } from '@store/network/networkSockets';

import type { NetworkSocketType } from '@store/network/networkSockets';

export const useInitContacts = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const gaEventTracker = useAnalyticsEventTracker();

  const { selectUserInfo } = useAppSelector((state) => state.network.network);
  const profile = useAppSelector((state) => state.profile.data);

  const theme = useTheme();
  const isTabletDisplay = useMediaQuery(theme.breakpoints.down(1200));

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    gaEventTracker({
      event: PageViewAnalyticsEvents.connectionAllMainPages,
      place: PagePlaceEnum.connectionContacts,
    });
    dispatch(setBreadcrumbs([{ title: t('general.breadcrumbs.contacts') }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, t]);

  useEffect(() => {
    if (profile?.id) {
      const contactChannel = SocketConnect.connect.private(`connections.${profile.id}`);
      SocketConnect.setChannel(`connections.${profile.id}`, contactChannel);

      const isMyContactPage =
        location.pathname === `${ContactsRoutesPathEnum.contacts}/${ContactsRoutesPathEnum.my_contacts}`;
      contactChannel
        .listen('.connections.created.contact', (event: NetworkSocketType) => {
          if (isMyContactPage) dispatch(networkSocketCreatedContact(event));
        })
        .listen('.connections.approved', (event: NetworkSocketType) => {
          dispatch(deleteNetworkUser(event.user.friend_id));
        });
    }

    return () => {
      if (profile?.id) {
        SocketConnect.connect.leave(`connections.${profile.id}`);
        SocketConnect.removeChannel(`connections.${profile.id}`);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, profile?.id, location.pathname]);

  useEffect(() => {
    const state = location.state;
    if (state?.userId) {
      getUserInfo({ dispatch, userId: state.userId });
    }
  }, [location, dispatch]);

  useEffect(() => {
    if (location.pathname === `${ContactsRoutesPathEnum.contacts}`) {
      navigate(`${ContactsRoutesPathEnum.contacts}/${ContactsRoutesPathEnum.favorites}`, {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return { selectUserInfo, isTabletDisplay };
};
